import CloseIcon from '@mui/icons-material/Close'
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Select,
    Typography,
} from '@mui/material'
import {
    BulkUploadDialogProps,
    BulkUploadDownloadActionsProps,
    BulkUploadDropzoneProps,
    BulkUploadSelectProps,
    BulkUploadUploadActionsProps,
} from './interface'

export const BulkUploadDialog = ({
    open,
    onClose,
    title,
    closeLabel,
    children,
}: BulkUploadDialogProps): JSX.Element => (
    <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
            sx: {
                borderRadius: 1,
                p: 1,
                minHeight: 450,
            },
        }}
    >
        <DialogTitle
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
            }}
        >
            <Typography variant="h6" sx={{ color: '#00858f', fontWeight: 700 }}>
                {title}
            </Typography>
            <IconButton
                id="bulk-upload-modal-close-button"
                aria-label={closeLabel}
                onClick={onClose}
                size="small"
            >
                <CloseIcon />
            </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 4, py: 2 }}>{children}</DialogContent>
    </Dialog>
)

export const BulkUploadSelect = ({
    id,
    label,
    value,
    onChange,
    options,
    placeholder,
    renderValue,
    sx = { mb: 2 },
}: BulkUploadSelectProps): JSX.Element => (
    <Box sx={sx}>
        <Typography
            variant="body1"
            sx={{ mb: 1, color: '#666', fontWeight: 500 }}
        >
            {label}
        </Typography>
        <Select
            id={id}
            value={value}
            onChange={onChange}
            variant="outlined"
            size="small"
            fullWidth
            displayEmpty
            MenuProps={{
                PaperProps: {
                    style: {
                        maxHeight: 30 * 12 + 8,
                    },
                },
            }}
            sx={{
                color: '#333',
                '.MuiOutlinedInput-notchedOutline': {
                    borderColor: '#ccc',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#999',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#00858f',
                },
            }}
            renderValue={renderValue}
        >
            <MenuItem value="" disabled>
                <Typography variant="body1" sx={{ fontSize: 14 }}>
                    {placeholder}
                </Typography>
            </MenuItem>
            {options.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                    {option.name}
                </MenuItem>
            ))}
        </Select>
    </Box>
)

export const BulkUploadDropzone = ({
    getRootProps,
    getInputProps,
    isDragActive,
    selectedFileName,
    selectFileLabel,
    dragAndDropLabel,
}: BulkUploadDropzoneProps): JSX.Element => (
    <Box
        id="bulk-upload-dropzone"
        {...getRootProps()}
        sx={{
            border: '2px dashed #4fd0dd',
            borderRadius: 2,
            bgcolor: '#e6fbfd',
            height: 180,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            mb: 3,
            transition: 'border .2s ease-in-out',
            ...(isDragActive && {
                borderColor: '#00858f',
                bgcolor: '#d0f5f8',
            }),
        }}
    >
        <input {...getInputProps()} />
        <img
            src="/assets/images/icons/upload.svg"
            alt="Upload"
            style={{
                width: 48,
                height: 48,
                marginBottom: 8,
            }}
        />
        {selectedFileName ? (
            <Typography
                variant="body1"
                sx={{ color: '#00858f', fontWeight: 500 }}
            >
                {selectedFileName}
            </Typography>
        ) : (
            <>
                <Typography
                    variant="body1"
                    sx={{
                        color: '#00858f',
                        fontWeight: 700,
                        mb: 0.5,
                    }}
                >
                    {selectFileLabel}
                </Typography>
                <Typography variant="body1" sx={{ color: '#8f9aa4' }}>
                    {dragAndDropLabel}
                </Typography>
            </>
        )}
    </Box>
)

export const BulkUploadUploadActions = ({
    uploadSuccess,
    successMessage,
    uploadFileLabel,
    onUpload,
    disabled,
}: BulkUploadUploadActionsProps): JSX.Element => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 3,
        }}
    >
        {uploadSuccess && (
            <Typography
                variant="body1"
                sx={{
                    color: '#3B3B3B',
                    fontSize: 14,
                    fontWeight: 500,
                }}
            >
                {successMessage}
            </Typography>
        )}

        <Button
            id="bulk-upload-submit-button"
            variant="outlined"
            onClick={onUpload}
            disabled={disabled}
            sx={{
                ml: 'auto',
                color: '#00858f',
                borderColor: '#00858f',
                px: 4,
                textTransform: 'none',
                '&:hover': {
                    borderColor: '#006d75',
                    bgcolor: 'rgba(0, 133, 143, 0.04)',
                },
                '&:disabled': {
                    borderColor: '#ccc',
                    color: '#999',
                },
            }}
        >
            {uploadFileLabel}
        </Button>
    </Box>
)

export const BulkUploadDownloadActions = ({
    onCancel,
    onDownload,
    disabled,
    cancelLabel,
    downloadLabel,
}: BulkUploadDownloadActionsProps): JSX.Element => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            mt: 2,
        }}
    >
        <Button
            id="bulk-upload-cancel-button"
            variant="outlined"
            sx={{
                textTransform: 'none',
                px: 4,
                borderColor: '#ccc',
                color: '#666',
                '&:hover': {
                    borderColor: '#999',
                    bgcolor: 'rgba(0,0,0,0.04)',
                },
            }}
            onClick={onCancel}
        >
            {cancelLabel}
        </Button>

        <Button
            id="bulk-upload-download-button"
            variant="outlined"
            sx={{
                textTransform: 'none',
                px: 4,
                '&:hover': {
                    borderColor: '#006d75',
                    bgcolor: 'rgba(0, 133, 143, 0.04)',
                },
                '&:disabled': {
                    borderColor: '#ccc',
                    color: '#999',
                },
            }}
            disabled={disabled}
            onClick={onDownload}
        >
            {downloadLabel}
        </Button>
    </Box>
)
